import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Upload,
  DollarSign,
  Search,
  Clock,
  Download,
  AlertCircle,
  CheckCircle2,
  Loader2,
  FileSpreadsheet,
} from "lucide-react";
import styles from "./ImportPatientModal.module.css";
import patientCallingService from "../../../services/patientCalling.service";
import * as XLSX from "xlsx";
import Toast from "../../Common/Toast";
import CommonPagination from "../../Common/CommonPagination";

interface Props {
  onClose: () => void;
  onImported: () => void;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ParsedRow {
  patient_name: string;
  patient_id: string;
  phone_number: string;
  date_of_service: string;
  insurance_name: string;
  _rowIndex: number;
  _errors: string[];
  _valid: boolean;
  [key: string]: any;
}

interface HistoryItem {
  id: string;
  file_name: string;
  call_type: string;
  created_at: string;
  status: string;
  success_count?: number;
  failed_count?: number;
  row_count?: number;
}

// Stores locally-generated result blobs keyed by history item id
type ResultBlobMap = Record<string, { blob: Blob; filename: string }>;

// ─── Constants ────────────────────────────────────────────────────────────────

const REQUIRED_HEADERS = [
  "patient_name",
  "patient_id",
  "phone_number",
  "date_of_service",
  "insurance_name",
];

const META_KEYS = new Set(["_rowIndex", "_errors", "_valid"]);

const statusClassFor = (status: string): string => {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "success") return styles.statusCompleted;
  if (s === "pending" || s === "processing" || s === "queued") return styles.statusPending;
  if (s === "failed" || s === "error") return styles.statusFailed;
  return styles.statusDefault;
};

const formatCallType = (type: string): string =>
  (type || "").replace(/_/g, " ");

const stripUuidPrefix = (filename: string): string =>
  filename.replace(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
    ""
  );

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const parseAndValidateFile = async (
  file: File
): Promise<{ headers: string[]; rows: Record<string, any>[] }> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });

  if (!json.length) throw new Error("File is empty.");

  const headers = Object.keys(json[0]);
  const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length) throw new Error(`Missing required columns: ${missing.join(", ")}`);

  return { headers, rows: json.map((row, i) => ({ ...row, _rowIndex: i + 2 })) };
};

const validateRows = (rows: Record<string, any>[]): ParsedRow[] =>
  rows.map((row) => {
    const errors: string[] = [];

    if (!String(row.patient_name ?? "").trim()) errors.push("Missing patient_name");
    if (!String(row.patient_id ?? "").trim()) errors.push("Missing patient_id");
    if (!String(row.insurance_name ?? "").trim()) errors.push("Missing insurance_name");
    if (!String(row.date_of_service ?? "").trim()) errors.push("Missing date_of_service");

    const phone = String(row.phone_number ?? "").trim().replace(/\D/g, "");
    if (!phone) errors.push("Missing phone_number");
    else if (!/^\d{10}$/.test(phone)) errors.push("phone_number must be 10 digits");

    return {
      ...row,
      _rowIndex: row._rowIndex ?? 0,
      _errors: errors,
      _valid: errors.length === 0,
    } as ParsedRow;
  });

/**
 * Builds an XLSX blob that contains:
 * - ALL original columns from the uploaded file (in original order)
 * - One extra "Upload Status" column appended at the end
 *
 * Status values:
 *   "Uploaded"           – row passed validation + backend confirmed success
 *   "Skipped: <reason>"  – backend said skipped
 *   "Failed: <reasons>"  – client-side validation failure or backend failure
 */
const generateResultFile = (
  validated: ParsedRow[],
  allHeaders: string[], // original column order from parsed file
  rowResults: Record<string, { status: string; reason?: string }> = {}
): Blob => {
  const columnOrder =
    allHeaders.length > 0
      ? allHeaders.filter((h) => !META_KEYS.has(h))
      : Object.keys(validated[0] ?? {}).filter((k) => !META_KEYS.has(k));

  const formatted = validated.map((row) => {
    let uploadStatus: string;

    if (!row._valid) {
      uploadStatus = `Failed: ${row._errors.join("; ")}`;
    } else {
      const r = rowResults[String(row.patient_id)];
      if (!r || r.status === "success" || r.status === "created") {
        uploadStatus = "Uploaded";
      } else if (r.status === "skipped") {
        uploadStatus = `Skipped: ${r.reason ?? ""}`;
      } else {
        uploadStatus = `Failed: ${r.reason ?? r.status}`;
      }
    }

    const out: Record<string, any> = {};
    columnOrder.forEach((col) => {
      out[col] = String(row[col] ?? "");
    });
    out["Upload Status"] = uploadStatus;
    return out;
  });

  const ws = XLSX.utils.json_to_sheet(formatted, {
    header: [...columnOrder, "Upload Status"],
  });
  ws["!cols"] = [...columnOrder.map(() => ({ wch: 22 })), { wch: 50 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Import Results");
  const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

// ─── Component ────────────────────────────────────────────────────────────────

const ImportPatientModal: React.FC<Props> = ({ onClose, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import flow
  const [callType, setCallType] = useState<"payment_reminder" | "patient_info_gathering">(
    "payment_reminder"
  );
  const [step, setStep] = useState<1 | 2>(1);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  // File + validation
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [validated, setValidated] = useState<ParsedRow[] | null>(null);
  const [parseError, setParseError] = useState<string>("");

  // History tab
  const [activeTab, setActiveTab] = useState<"import" | "history">("import");
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalImports, setTotalImports] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Locally generated result blobs — populated after each upload in this session
  // Keyed by the history item id returned by the backend
  const [resultBlobs, setResultBlobs] = useState<ResultBlobMap>({});
  const [resultFilename, setResultFilename] = useState<string>("");
  
  // Toast — rendered inside the modal, positioned top-right
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
  } | null>(null);

  // Derived counts
  const validCount = validated?.filter((r) => r._valid).length ?? 0;
  const invalidCount = validated?.filter((r) => !r._valid).length ?? 0;

  // ── History ────────────────────────────────────────────────────────────────

  const loadHistory = async () => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const res = await patientCallingService.getImportHistory({ page, page_size: pageSize });
      setHistory(res?.items || []);
      setTotalImports(res?.total ?? 0);
    } catch (e: any) {
      setHistory([]);
      setTotalImports(0);
      setHistoryError(e.message || "Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") loadHistory();
  }, [page, pageSize, activeTab]);

  // ── File selection ─────────────────────────────────────────────────────────

  const handleFileSelected = async (file: File) => {
    setParseError("");
    setValidated(null);
    setFileHeaders([]);
    setSelectedFile(file);
    try {
      const { headers, rows } = await parseAndValidateFile(file);
      setFileHeaders(headers);
      setValidated(validateRows(rows));
    } catch (e: any) {
      setParseError(e.message || "Failed to parse file");
    }
  };

  // ── Upload ─────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!validated || !selectedFile) return;

    setLoading(true);
    try {
      const res = await patientCallingService.importFile(selectedFile, callType);

      // Backend may return per-row results keyed by patient_id
      const rowResults: Record<string, { status: string; reason?: string }> =
        res?.row_results ?? {};

      // Build the annotated result XLSX — stored in state, NOT auto-downloaded.
      // User downloads it manually via the History tab download icon.
      const blob = generateResultFile(validated, fileHeaders, rowResults);
      setResultBlob(blob);

      const filename = `patient_import_result_${new Date().toISOString().slice(0, 10)}.xlsx`;
      setResultFilename(filename);
      // Map the blob to the backend history id so the history row can serve it
      const historyId: string | undefined = res?.import_session_id;
      if (historyId) {
        setResultBlobs((prev) => ({ ...prev, [historyId]: { blob, filename } }));
      }

      const imported = res?.imported ?? validCount;
      setToast({
        type: "success",
        message: `${imported} record${imported !== 1 ? "s" : ""} imported. Go to History to download results.`,
      });

      onImported();

      // Auto-switch to History tab after a short pause so the toast is readable
      setTimeout(() => setActiveTab("history"), 1800);
    } catch (e: any) {
      setToast({
        type: "error",
        message: e.message || "Import failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── History download ───────────────────────────────────────────────────────

  /**
   * Download priority:
   * 1. Locally generated XLSX blob (includes "Upload Status" column) — preferred.
   * 2. Backend URL fallback (original file, no status column).
   */
  const handleDownloadResult = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a   = document.createElement("a");
    a.href = url; a.download = resultFilename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const res  = await patientCallingService.getImportDownloadUrl(id);
      const link = document.createElement("a");
      link.href = res.url; link.download = ""; link.click();
    } catch (err) { console.error("Download failed", err); }
    finally { setDownloadingId(null); }
  };

  // ── Back ───────────────────────────────────────────────────────────────────

  const handleBack = () => {
    setStep(1);
    setValidated(null);
    setFileHeaders([]);
    setParseError("");
    setSelectedFile(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative" }}
      >

        {/*
         * TOAST — anchored to the top-right corner of the modal.
         * Rendered before the header so it overlays everything else.
         * right: 52px leaves room for the × close button.
         */}
        {toast && (
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 52,
              zIndex: 200,
              minWidth: 260,
              maxWidth: 340,
              pointerEvents: "auto",
            }}
          >
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          </div>
        )}

        {/* ── HEADER ── */}
        <div className={styles.header}>
          <h2 className={styles.modalTitle}>
            {activeTab === "history"
              ? "Import History"
              : step === 1
              ? "Select Call Type"
              : "Upload File"}
          </h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* ── TABS ── */}
        <div className={styles.tabBar}>
          <button
            className={`${styles.tabBtn} ${activeTab === "import" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("import")}
          >
            <Upload size={14} /> Import
          </button>
          <button
            className={`${styles.tabBtn} ${activeTab === "history" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("history")}
          >
            <Clock size={14} /> History
          </button>
        </div>

        {/* ── BODY ── */}
        <div className={styles.body}>

          {/* =========== IMPORT TAB =========== */}
          {activeTab === "import" && (
            <>
              {/* STEP 1 — Call Type */}
              {step === 1 && (
                <div className={styles.callTypeGrid}>
                  <div
                    className={`${styles.callTypeCard} ${
                      callType === "payment_reminder" ? styles.callTypeCardActive : ""
                    }`}
                    onClick={() => setCallType("payment_reminder")}
                  >
                    <DollarSign size={22} />
                    <div className={styles.callTypeCardTitle}>Payment Reminder</div>
                    <div className={styles.callTypeCardDesc}>
                      Remind patients about outstanding balances
                    </div>
                  </div>

                  <div
                    className={`${styles.callTypeCard} ${
                      callType === "patient_info_gathering" ? styles.callTypeCardActive : ""
                    }`}
                    onClick={() => setCallType("patient_info_gathering")}
                  >
                    <Search size={22} />
                    <div className={styles.callTypeCardTitle}>Info Gathering</div>
                    <div className={styles.callTypeCardDesc}>
                      Collect missing insurance info
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2 — Upload + Validation Preview */}
              {step === 2 && (
                <>
                  {/* Parse error */}
                  {parseError && (
                    <div className={styles.errorBanner}>
                      <AlertCircle size={14} style={{ flexShrink: 0 }} />
                      {parseError}
                    </div>
                  )}

                  {/* Invalid row warning */}
                  {!parseError && invalidCount > 0 && (
                    <div className={styles.errorBanner}>
                      <AlertCircle size={14} style={{ flexShrink: 0 }} />
                      {invalidCount} invalid row{invalidCount !== 1 ? "s" : ""} detected — fix
                      them before uploading.
                    </div>
                  )}

                  {/* Dropzone */}
                  <div
                    className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleFileSelected(file);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={28} />
                    <div>
                      <strong>
                        {selectedFile ? selectedFile.name : "Click or drag file"}
                      </strong>
                    </div>
                    <small>
                      {selectedFile ? "Click to replace" : "CSV, XLS, XLSX supported"}
                    </small>
                    {loading && <div className={styles.uploading}>Uploading…</div>}
                  </div>

                  {/* Validation preview — shows summary + invalid rows only */}
                  {validated && !parseError && (
                    <div className={styles.previewSection}>
                      {/* Summary badges */}
                      <div className={styles.previewHeader}>
                        <span className={styles.previewTitle}>
                          Preview — {validated.length} row{validated.length !== 1 ? "s" : ""}
                        </span>
                        <div className={styles.badgeRow}>
                          <span className={styles.badgeGreen}>
                            <CheckCircle2 size={11} /> {validCount} valid
                          </span>
                          {invalidCount > 0 && (
                            <span className={styles.badgeRed}>
                              <AlertCircle size={11} /> {invalidCount} invalid
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Table of invalid rows */}
                      {invalidCount > 0 && (
                        <div className={styles.tableWrap}>
                          <table className={styles.previewTable}>
                            <thead>
                              <tr>
                                <th>Row</th>
                                <th>patient_name</th>
                                <th>patient_id</th>
                                <th>phone_number</th>
                                <th>date_of_service</th>
                                <th>insurance_name</th>
                                <th>Issues</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validated
                                .filter((r) => !r._valid)
                                .slice(0, 10)
                                .map((row, i) => (
                                  <tr key={i} className={styles.rowInvalid}>
                                    <td>{row._rowIndex}</td>
                                    <td>{row.patient_name || <span className={styles.emptyCell}>—</span>}</td>
                                    <td>{row.patient_id || <span className={styles.emptyCell}>—</span>}</td>
                                    <td>{row.phone_number || <span className={styles.emptyCell}>—</span>}</td>
                                    <td>{row.date_of_service || <span className={styles.emptyCell}>—</span>}</td>
                                    <td>{row.insurance_name || <span className={styles.emptyCell}>—</span>}</td>
                                    <td>
                                      <span
                                        className={styles.badgeRed}
                                        title={row._errors.join(", ")}
                                      >
                                        <AlertCircle size={11} /> {row._errors.join(", ")}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          {invalidCount > 10 && (
                            <div className={styles.moreRows}>
                              +{invalidCount - 10} more invalid rows not shown
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelected(file);
                  e.target.value = ""; // allow re-picking same file
                }}
              />
            </>
          )}

          {/* =========== HISTORY TAB =========== */}
          {activeTab === "history" && (
            <div className={styles.historyPanel}>
              {/* Toolbar */}
              <div className={styles.historyToolbar}>
                <span className={styles.historyCount}>
                  {totalImports} import{totalImports !== 1 ? "s" : ""}
                </span>
                <button
                  className={styles.refreshBtn}
                  onClick={loadHistory}
                  disabled={loadingHistory}
                >
                  {loadingHistory && (
                    <Loader2 size={12} className={styles.spinning} />
                  )}
                  Refresh
                </button>
              </div>

              {historyError && (
                <div className={styles.errorBanner}>
                  <AlertCircle size={14} style={{ flexShrink: 0 }} />
                  {historyError}
                </div>
              )}

              {loadingHistory && !history.length && (
                <div className={styles.historyEmpty}>Loading…</div>
              )}
              {!loadingHistory && history.length === 0 && (
                <div className={styles.historyEmpty}>No imports yet</div>
              )}

              <div className={styles.historyList}>
                {history.map((item) => {
                  const isExpanded = expandedId === item.id;
                  // True when this session generated a result blob for this import
                  const hasLocalBlob = !!resultBlobs[item.id];

                  return (
                    <div key={item.id} className={styles.historyCard}>
                      {/* Card header row */}
                      <div
                        className={styles.historyCardHeader}
                        onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      >
                        <div className={styles.historyCardLeft}>
                          <FileSpreadsheet
                            size={18}
                            className={styles.historyFileIcon}
                          />
                          <div className={styles.historyCardInfo}>
                            <div
                              className={styles.historyFilename}
                              title={stripUuidPrefix(item.file_name)}
                            >
                              {stripUuidPrefix(item.file_name)}
                            </div>
                            <div className={styles.historyMeta}>
                              <span className={styles.callTypeChip}>
                                {formatCallType(item.call_type)}
                              </span>
                              <span className={styles.historyMetaDot}>•</span>
                              <span>
                                {new Date(item.created_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className={styles.historyCardRight}>
                          <span
                            className={`${styles.statusBadge} ${statusClassFor(item.status)}`}
                          >
                            {item.status}
                          </span>

                          {/*
                           * Download icon:
                           * - If we have a local blob → serves annotated XLSX with Upload Status
                           * - If completed but no local blob → falls back to backend URL
                           * - Shows a spinner while fetching the backend URL
                           */}
                          {(item.status === "completed" || hasLocalBlob) && (
                            <button
                              className={styles.downloadBtn}
                              disabled={downloadingId === item.id}
                              title={
                                hasLocalBlob
                                  ? "Download results with Upload Status"
                                  : "Download original file"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (hasLocalBlob) {
                                  const { blob, filename } = resultBlobs[item.id];
                                  triggerDownload(blob, filename);
                                } else {
                                  handleDownload(item.id);
                                }
                              }}
                            >
                              {downloadingId === item.id ? (
                                <Loader2 size={15} className={styles.spinning} />
                              ) : (
                                <Download size={15} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className={styles.historyCardBody}>
                          <div className={styles.outcomeSummary}>
                            <span className={styles.outcomeSuccess}>
                              ✅ {item.success_count ?? 0} succeeded
                            </span>
                            <span className={styles.outcomeFailed}>
                              ❌ {item.failed_count ?? 0} failed
                            </span>
                            <span className={styles.outcomeTotal}>
                              📄 {item.row_count ?? 0} total
                            </span>
                          </div>
                          {hasLocalBlob && (
                            <p
                              className={styles.historyMeta}
                              style={{ marginTop: 6, color: "#16a34a" }}
                            >
                              ✓ Result file with "Upload Status" column ready to download
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div
          className={`${styles.footer} ${
            activeTab === "history" ? styles.footerPagination : ""
          }`}
        >
          {activeTab === "import" ? (
            step === 1 ? (
              <>
                <button className={styles.outlineBtn} onClick={onClose}>
                  Cancel
                </button>
                <button className={styles.primaryBtn} onClick={() => setStep(2)}>
                  Next
                </button>
              </>
            ) : (
              <>
                <button className={styles.outlineBtn} onClick={handleBack}>
                  Back
                </button>
                <button className={styles.outlineBtn} onClick={onClose}>
                  Cancel
                </button>
                <button
                  className={styles.primaryBtn}
                  disabled={
                    !validated ||
                    !!parseError ||
                    validCount === 0 ||
                    invalidCount > 0 ||
                    loading
                  }
                  title={
                    invalidCount > 0
                      ? `Fix ${invalidCount} invalid row${invalidCount !== 1 ? "s" : ""} first`
                      : undefined
                  }
                  onClick={handleUpload}
                >
                  {loading ? (
                    <>
                      <Loader2 size={14} className={styles.spinning} /> Uploading…
                    </>
                  ) : (
                    `Upload${validCount > 0 ? ` (${validCount})` : ""}`
                  )}
                </button>
              </>
            )
          ) : (
            totalImports > 0 && (
              <CommonPagination
                show
                renderInPlace
                pageCount={Math.max(1, Math.ceil(totalImports / pageSize))}
                currentPage={page - 1}
                onPageChange={({ selected }) => setPage(selected + 1)}
                totalItems={totalImports}
                itemsPerPage={pageSize}
                onItemsPerPageChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            )
          )}
        </div>

      </div>
    </div>
  );
};

export default ImportPatientModal;